import { EventEmitter } from '@angular/core';
import { CRUD_ROUTE_STATE_LIST } from '@common/crud/frontend-shared';
import { ItemServiceHelper } from '@core/frontend-shared/api';
import { convertToBehaviorSubject, getDiff, objectCloneDeep } from '@core/shared/utils';
import { CrudSearchQueryBuilder } from '@core/shared/crud';
import { CondOperator } from '@nestjsx/crud-request/lib/types';
import { BehaviorSubject, Subject, of, timer } from 'rxjs';
import { debounce, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import * as deepmerge from 'deepmerge';
export const defaultCrudListState = {
  paging: {
    itemsPerPage: 20,
    offset: 0
  },
  ordering: null,
  filters: {},
  permanentFilters: null
};
export class ListStateEvent {}
// event fired when pagination offset is reset programmatically due to changed filter/ordering
export class ResetPaginationEvent extends ListStateEvent {
  constructor(offset, itemsPerPage) {
    super();
    this.offset = offset;
    this.itemsPerPage = itemsPerPage;
  }
}
export class CrudListController {
  constructor(listConfig, service, state, modelColumnService, dependenciesFactory) {
    this.listConfig = listConfig;
    this.service = service;
    this.state = state;
    this.modelColumnService = modelColumnService;
    this.dependenciesFactory = dependenciesFactory;
    this.currentOrdering$ = new BehaviorSubject(null);
    this.reorderingPermitted$ = this.currentOrdering$.pipe(map(ordering => {
      return ordering === null || ordering.field === 'ordering';
    }));
    this.contextGroups$ = new BehaviorSubject([]);
    // note that this ignores state.permanentFilters. It only reacts to filterFormService filters object.
    this.hasActiveFilters$ = new BehaviorSubject(false);
    this.filterFormService = null;
    this.isLoadingDependencies$ = new BehaviorSubject(false);
    this.listState$ = new BehaviorSubject(defaultCrudListState);
    this.listStateEvents$ = new EventEmitter();
    this.lockedState = {};
    this.dependencies = null;
    this.initialized = false;
    this.destroy$ = new Subject();
    this.itemServiceHelper = new ItemServiceHelper(this.service);
    this.items$ = new BehaviorSubject(null);
    this.validate();
    this.preloadDependencies();
    if (this.listConfig.loadAllItems) {
      this.service.query.setLimit(null);
      this.updateListState({
        paging: {
          offset: null,
          itemsPerPage: null
        }
      });
    } else if (typeof this.listConfig.itemsPerPage === 'number') {
      this.updateListState({
        paging: {
          offset: 0,
          itemsPerPage: this.listConfig.itemsPerPage
        }
      });
    }
    this.rangeInfo$ = this.service.getRangeInfo$().pipe(takeUntil(this.destroy$));
    this.listState$.pipe(debounce(s => {
      return typeof s?.debounce === 'number' ? timer(s.debounce) : of(true);
    }), takeUntil(this.destroy$)).subscribe(currentState => {
      this.handleUpdatedListState(currentState);
    });
    this.itemColumns$ = convertToBehaviorSubject(null)(this.contextGroups$.pipe(switchMap(contextGroups => {
      const cols$ = this.modelColumnService.getModelColumns(this.service.ItemFactory.Model, contextGroups, this.listConfig.cols).pipe(
      // tap(data=>{
      // 	console.log('cols$ emitted data',data)
      // }),
      filter(data => data !== null));
      return cols$;
    })));
  }
  validate() {
    if (typeof this.service === 'undefined') throw new Error('ItemTableComponent requires a [service]!');
    if (this.listConfig.reordering && !this.listConfig.loadAllItems) throw new Error('Reorderable table must not be paged. Set crudConfig.list.loadAllItems to true to fix!');
  }
  preloadDependencies() {
    if (this.dependenciesFactory) {
      this.isLoadingDependencies$.next(true);
      this.dependenciesFactory().subscribe(deps => {
        this.dependencies = deps;
        this.isLoadingDependencies$.next(false);
      });
    }
  }
  setContextGroups(groups) {
    this.contextGroups$.next(groups);
  }
  createItemsObservable() {
    return this.items$;
  }
  getSubscriptions() {
    return {
      rangeInfo$: this.rangeInfo$,
      loadingState$: this.service.loadingState$,
      isLoadingDependencies$: this.isLoadingDependencies$,
      currentOrdering$: this.currentOrdering$,
      reorderingPermitted$: this.reorderingPermitted$,
      hasActiveFilters$: this.hasActiveFilters$,
      listStateEvents$: this.listStateEvents$,
      contextGroups$: this.contextGroups$,
      itemColumns$: this.itemColumns$
    };
  }
  destroy() {
    this.destroy$.next(true);
  }
  // -------------  LIST STATE  -----------------------------------------------------------------
  /**
   * to initialize API fetching, either call updateListState or do it manually by calling loadItems.
   */
  // allows to lock properties of listState to be non-customizable via updateListState.
  lockListState(state) {
    if (!IS_PRODUCTION && state.filters) {
      throw new Error('CrudListState.filters cannot be locked. Please pass filters in using .permanentFilters!');
    }
    const diff = getDiff(state, this.lockedState);
    const hasChanges = Object.keys(diff).length > 0;
    if (hasChanges) {
      // console.log('modify locked list state', Object.keys(diff), diff)
      this.lockedState = state;
      this.updateListState({});
    }
  }
  updateListState(patch, debounceMs = 10) {
    const oldState = this.listState$.getValue();
    // mergeing state sources was done using intransparent custom logic interacting with deepmerge-ts strategies.
    // tthis was hard to understand. Turns out that in case of most subobjects, we don't want a merge but overwrite strategy.
    // so we do it manually now.
    const ordering = this.lockedState.ordering || patch.ordering || oldState.ordering;
    const filters = this.lockedState.filters || patch.filters || oldState.filters;
    const permanentFilters = this.lockedState.permanentFilters || patch.permanentFilters || oldState.permanentFilters;
    const paging = deepmerge.all([oldState.paging, patch.paging || {}, this.lockedState.paging || {}]);
    let newState = objectCloneDeep({
      paging,
      ordering,
      filters,
      permanentFilters,
      debounce: debounceMs
    });
    const diff = getDiff(oldState, newState);
    // side effect: reset filterFormService
    if (patch.filters && Object.keys(patch.filters).length === 0) this.filterFormService.setValue({});
    const resetPagingIfNotExplicitlySet = () => {
      if (!diff.paging) newState.paging.offset = 0;
      this.listStateEvents$.emit(new ResetPaginationEvent(newState.paging.offset, newState.paging.itemsPerPage));
    };
    if (diff.filters) resetPagingIfNotExplicitlySet();
    if (diff.ordering) resetPagingIfNotExplicitlySet();
    this.listState$.next(newState);
  }
  loadItems() {
    if (this.initialized) return;
    this.initialized = true;
    // call read with forced refresh to make sure data is being reloaded e.g. after re-login
    this.service.read(false, true).pipe(takeUntil(this.destroy$)).subscribe(items => {
      this.items$.next(items);
    });
  }
  reloadItems() {
    if (!this.initialized) throw new Error('CrudListController::reloadItems called before being initialized!');
    if (!this.service.hasReadItems) return;
    // service will emit new data to observable connected in loadItems!
    this.service.refreshAllItems().subscribe();
  }
  persistState(id) {
    if (!IS_PRODUCTION) console.warn('listController.persistState is not implemented yet!');
  }
  handleUpdatedListState(state) {
    // const diff = getDiff(state, prevState);
    this.service.query.setSortBy(null);
    this.service.setVirtualSortBy(null);
    if (state.ordering?.length) {
      if (state.ordering.length > 1) throw new Error('Sorting by multiple columns is not supported currently, if needed remove this error and finish implementation');
      const itemColumnConfigs = this.itemColumns$.getValue();
      state.ordering.forEach(ordering => {
        const colInfo = itemColumnConfigs ? itemColumnConfigs.find(col => col.field === ordering.field) : null;
        this._applySorting(ordering.field, ordering.order, colInfo?.clientSorting || undefined);
      });
    }
    this.itemServiceHelper.applyPaging(state.paging.offset, state.paging.itemsPerPage);
    this._updateFilters(state.filters, state.permanentFilters);
    if (!this.initialized) this.loadItems();else this.reloadItems();
  }
  // -------------  ORDERING/SORTING  -----------------------------------------------------------------
  getDefaultSortField() {
    return this.state?.[CRUD_ROUTE_STATE_LIST]?.ordering?.field || this.listConfig.sortField || 'id';
  }
  getDefaultSortOrdering() {
    const orderDirection = this.state?.[CRUD_ROUTE_STATE_LIST]?.ordering?.ordering || this.listConfig.sortOrdering;
    return orderDirection === 'DESC' ? -1 : 1;
  }
  _applySorting(sortField, sortOrder, clientSorting) {
    const order = sortOrder === -1 ? 'DESC' : 'ASC';
    if (clientSorting) {
      // frontend sorting
      if (!this.listConfig.loadAllItems) throw new Error('list column ' + sortField + ' has a clientSorting method but loadAllItems is disabled!');
      const requiresDependencies = this.sortingFunctionRequiresDependencies(clientSorting);
      if (!requiresDependencies) {
        this.service.setVirtualSortBy(clientSorting, order);
      } else {
        if (!this.isLoadingDependencies$.getValue()) {
          this.service.setVirtualSortBy((a, b) => {
            return clientSorting(a, b, this.dependencies);
          }, order);
        } else {
          // need to wait for dependencies to load before sorting can be applied!
          throw new Error('Oops! need to wait for dependencies to load before sorting can be applied! Not implemented!');
          // setTimeout(()=>{ this.reloadItems(event) },1000)
        }
      }
    } else {
      // backend sorting
      const newOrdering = {
        field: sortField,
        order
      };
      const curr = this.currentOrdering$.value;
      if (curr === null || curr.field !== newOrdering.field || curr.order !== newOrdering.order) {
        this.currentOrdering$.next(newOrdering);
        this.service.query.setSortBy(newOrdering);
      }
    }
  }
  sortingFunctionRequiresDependencies(func) {
    return func.length > 2;
  }
  // -------------  FILTERING  -----------------------------------------------------------------
  bindFilterFieldsetFormService(service) {
    this.filterFormService = service;
    if (this.state?.[CRUD_ROUTE_STATE_LIST]?.filters) {
      const filters = this.state?.[CRUD_ROUTE_STATE_LIST].filters;
      service.patchValue(filters);
      service.resetFormFieldValidation();
      this.updateListState({
        filters
      });
      // this.updateFilters(filters)
    }
  }
  getFilterFormService() {
    return this.filterFormService;
  }
  _updateFilters(filterState, permanentFilters) {
    if (!filterState) filterState = {}; // can be null sometimes, handle that situation
    if (!permanentFilters) permanentFilters = [];
    // do not set loading state here! 
    this.service.query.setFilter(null);
    this.service.query.setOr(null);
    this.hasActiveFilters$.next(false);
    const builtListFilters = this.listConfig.filters.map(filterField => {
      const filterName = filterField.config.formControlName;
      const filterValue = typeof filterState[filterName] !== 'undefined' ? filterState[filterName] : null;
      // some fields have multiple filters assigned... e.g. text search within ID + name
      const availableFilters = filterField.config.filters.map((f, i) => {
        return {
          field: f.field,
          value: filterValue,
          operator: f.operator
        };
      });
      return availableFilters;
    });
    // Depending on type of filter, the value switches its state on and off.
    // For other types like $isnull, it is always enabled no matter what's the value.
    const activeFilters = builtListFilters.reduce((collection, filterGroup) => {
      const filtered = filterGroup.filter(f => {
        if (f.operator === CondOperator.IS_NULL || f.operator === CondOperator.NOT_NULL) return true;
        const isEmptyArray = Array.isArray(f.value) && f.value.length === 0;
        return f.value !== null && f.value !== '' && !isEmptyArray;
      });
      if (filtered.length) collection.push(filtered);
      return collection;
    }, []);
    // first dimension to be treated as AND, second dimension treated as OR
    const crudQueryFilters = [];
    crudQueryFilters.push(...activeFilters);
    // hasActiveFilters should not be influenced by permanentFilters as user is not controlling these
    if (activeFilters.length) this.hasActiveFilters$.next(true);
    crudQueryFilters.push(...permanentFilters);
    const search = new CrudSearchQueryBuilder();
    crudQueryFilters.forEach(filterGroup => {
      if (filterGroup.length === 1) {
        const filter = filterGroup[0];
        search.fromQueryFilter('$and', filter);
      } else {
        search.createSubSearch('$or', subSearch => {
          filterGroup.forEach(filter => {
            subSearch.fromQueryFilter('$and', filter);
          });
        });
      }
    });
    this.service.query.setSearch(search);
    // console.log('built crud search',customFilters,search.getSearchQuery(), this.service.query.queryObject);
    // crudQueryFilters.forEach(filterGroup=>{
    // 	const multiple = filterGroup.length>1;
    // 	filterGroup.forEach(filter=>{
    // 		if(multiple) this.service.query.setOr(filter);
    // 		else this.service.query.setFilter(filter);
    // 	})
    // })
  }
}